import React, { useState, useEffect } from 'react';
import { shopAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const STATUS_LABELS = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  abgeschlossen: 'Abgeschlossen'
};

const EMPTY_FORM = {
  name: '',
  description: '',
  price: '',
  imageUrl: '',
  sizes: 'S, M, L, XL',
  sortOrder: 0,
  active: true
};

const ShopTab = () => {
  const [activeSection, setActiveSection] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paidFilter, setPaidFilter] = useState('all');

  const loadProducts = async () => {
    try {
      const response = await shopAPI.getAllProducts();
      if (response.success) setProducts(response.products || []);
    } catch (error) {
      console.error('Fehler beim Laden der Artikel:', error);
      toast.error('Fehler beim Laden der Artikel');
    }
  };

  const loadOrders = async () => {
    try {
      const response = await shopAPI.getAllOrders();
      if (response.success) setOrders(response.orders || []);
    } catch (error) {
      console.error('Fehler beim Laden der Bestellungen:', error);
      toast.error('Fehler beim Laden der Bestellungen');
    }
  };

  useEffect(() => {
    if (activeSection === 'products') {
      loadProducts();
    } else {
      loadOrders();
    }
  }, [activeSection]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await shopAPI.uploadImage(file);
      setProductForm(prev => ({ ...prev, imageUrl: response.imageUrl }));
      toast.success('Bild hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const openNewProduct = () => {
    setEditingProductId(null);
    setProductForm(EMPTY_FORM);
    setShowProductModal(true);
  };

  const openEditProduct = (product) => {
    setEditingProductId(product.id);
    setProductForm({
      name: product.name,
      description: product.description || '',
      price: product.price,
      imageUrl: product.imageUrl || '',
      sizes: (product.sizes || []).join(', '),
      sortOrder: product.sortOrder || 0,
      active: product.active
    });
    setShowProductModal(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: productForm.name,
        description: productForm.description,
        price: productForm.price,
        imageUrl: productForm.imageUrl,
        sizes: productForm.sizes,
        active: productForm.active,
        sortOrder: productForm.sortOrder
      };

      const response = editingProductId
        ? await shopAPI.updateProduct(editingProductId, payload)
        : await shopAPI.createProduct(payload);

      if (response.success) {
        toast.success(editingProductId ? 'Artikel aktualisiert' : 'Artikel angelegt');
        setShowProductModal(false);
        setEditingProductId(null);
        await loadProducts();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Artikel konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Artikel "${product.name}" wirklich löschen? Bestehende Bestellungen bleiben erhalten.`)) return;
    try {
      await shopAPI.deleteProduct(product.id);
      toast.success('Artikel gelöscht');
      await loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Artikel konnte nicht gelöscht werden');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await shopAPI.updateProduct(product.id, {
        name: product.name,
        description: product.description,
        price: product.price,
        imageUrl: product.imageUrl,
        sizes: (product.sizes || []).join(', '),
        active: !product.active,
        sortOrder: product.sortOrder
      });
      await loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Status konnte nicht geändert werden');
    }
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      await shopAPI.updateOrder(orderId, { status });
      toast.success('Status aktualisiert');
      await loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Status konnte nicht geändert werden');
    }
  };

  const handlePaidToggle = async (order) => {
    try {
      await shopAPI.updateOrder(order.id, { paid: !order.paid });
      toast.success(!order.paid ? 'Als bezahlt markiert' : 'Zahlung zurückgesetzt');
      await loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Zahlstatus konnte nicht geändert werden');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Bestellung Nr. ${order.id} von ${order.memberName} wirklich löschen?`)) return;
    try {
      await shopAPI.deleteOrder(order.id);
      toast.success('Bestellung gelöscht');
      await loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Bestellung konnte nicht gelöscht werden');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesPaid = paidFilter === 'all'
      || (paidFilter === 'paid' && order.paid)
      || (paidFilter === 'unpaid' && !order.paid);
    return matchesStatus && matchesPaid;
  });

  // Eine Zeile je Position, damit sich die Liste nach Artikel und Groesse
  // sortieren laesst - so sieht man auf einen Blick, was zu bestellen ist.
  const exportOrdersToExcel = () => {
    const rows = [];
    filteredOrders.forEach(order => {
      order.items.forEach(item => {
        rows.push({
          'Bestell-Nr.': order.id,
          'Datum': new Date(order.createdAt).toLocaleDateString('de-DE'),
          'Mitglied': order.memberName,
          'E-Mail': order.email,
          'Artikel': item.productName,
          'Größe': item.size,
          'Menge': item.quantity,
          'Einzelpreis': item.unitPrice,
          'Positionssumme': Number((item.unitPrice * item.quantity).toFixed(2)),
          'Status': STATUS_LABELS[order.status] || order.status,
          'Bezahlt': order.paid ? 'Ja' : 'Nein'
        });
      });
    });

    if (rows.length === 0) {
      toast.info('Keine Bestellungen zum Exportieren');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 11 }, { wch: 12 }, { wch: 22 }, { wch: 28 }, { wch: 24 },
      { wch: 8 }, { wch: 7 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 9 }
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bestellungen');
    XLSX.writeFile(workbook, `EFC_Bestellungen_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Bestellungen wurden als Excel exportiert!');
  };

  const openOrders = orders.filter(o => o.status === 'offen').length;

  return (
    <div className="shop-tab">
      <div className="users-header">
        <h2>Klamotten</h2>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeSection === 'products' ? 'active' : ''}`}
          onClick={() => setActiveSection('products')}
        >
          <span className="icon">👕</span>
          Artikel
        </button>
        <button
          className={`tab ${activeSection === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveSection('orders')}
        >
          <span className="icon">📦</span>
          Bestellungen{openOrders > 0 ? ` (${openOrders} offen)` : ''}
        </button>
      </div>

      {activeSection === 'products' && (
        <div className="members-section">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
            <h3>Artikel ({products.length})</h3>
            <button onClick={openNewProduct} className="btn btn-primary">
              + Artikel hinzufügen
            </button>
          </div>

          {products.length === 0 ? (
            <div className="empty-state">
              <p>Noch keine Artikel angelegt.</p>
            </div>
          ) : (
            <div className="games-table-wrapper">
              <table className="games-table shop-table">
                <thead>
                  <tr>
                    <th>Bild</th>
                    <th>Name</th>
                    <th>Preis</th>
                    <th>Größen</th>
                    <th>Sichtbar</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.id}>
                      <td>
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            style={{width: '48px', height: '48px', objectFit: 'contain', background: '#f4f4f4', borderRadius: '4px'}}
                          />
                        ) : '—'}
                      </td>
                      <td className="opponent-cell"><strong>{product.name}</strong></td>
                      <td>{product.price.toFixed(2)} €</td>
                      <td className="shop-fill">{(product.sizes || []).join(', ')}</td>
                      <td>
                        <button
                          className={`status-badge status-${product.active ? 'open' : 'past'}`}
                          onClick={() => handleToggleActive(product)}
                          title="Sichtbarkeit umschalten"
                          style={{cursor: 'pointer', border: 'none'}}
                        >
                          {product.active ? 'Aktiv' : 'Inaktiv'}
                        </button>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-secondary" title="Bearbeiten" onClick={() => openEditProduct(product)}>✏️</button>
                          <button className="btn btn-sm btn-danger" title="Löschen" onClick={() => handleDeleteProduct(product)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'orders' && (
        <div className="members-section">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
            <h3>Bestellungen ({filteredOrders.length})</h3>
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">Alle Status</option>
                <option value="offen">Offen</option>
                <option value="in_bearbeitung">In Bearbeitung</option>
                <option value="abgeschlossen">Abgeschlossen</option>
              </select>
              <select value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}>
                <option value="all">Bezahlt und offen</option>
                <option value="paid">Nur bezahlt</option>
                <option value="unpaid">Nur unbezahlt</option>
              </select>
              <button onClick={exportOrdersToExcel} className="btn btn-success" disabled={filteredOrders.length === 0}>
                📊 Excel Export
              </button>
            </div>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="empty-state">
              <p>Keine Bestellungen vorhanden.</p>
            </div>
          ) : (
            <div className="games-table-wrapper">
              <table className="games-table shop-table">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Datum</th>
                    <th>Mitglied</th>
                    <th>Positionen</th>
                    <th>Summe</th>
                    <th>Status</th>
                    <th>Bezahlt</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => (
                    <tr key={order.id}>
                      <td>{order.id}</td>
                      <td>{new Date(order.createdAt).toLocaleDateString('de-DE')}</td>
                      <td className="opponent-cell">
                        <strong>{order.memberName}</strong><br/>
                        <small>{order.email}</small>
                      </td>
                      <td className="shop-fill">
                        {order.items.map(item => (
                          <div key={item.id}>
                            {item.quantity}× {item.productName} (Größe {item.size})
                          </div>
                        ))}
                        {order.note && <div><small><em>Nachricht: {order.note}</em></small></div>}
                      </td>
                      <td>{order.total.toFixed(2)} €</td>
                      <td>
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        >
                          <option value="offen">Offen</option>
                          <option value="in_bearbeitung">In Bearbeitung</option>
                          <option value="abgeschlossen">Abgeschlossen</option>
                        </select>
                      </td>
                      <td>
                        <button
                          className={`status-badge status-${order.paid ? 'open' : 'past'}`}
                          onClick={() => handlePaidToggle(order)}
                          style={{cursor: 'pointer', border: 'none'}}
                          title={order.paid ? 'Zahlung zurücksetzen' : 'Als bezahlt markieren'}
                        >
                          {order.paid ? 'Bezahlt' : 'Offen'}
                        </button>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-danger" title="Löschen" onClick={() => handleDeleteOrder(order)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showProductModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingProductId ? 'Artikel bearbeiten' : 'Neuer Artikel'}</h2>
              <button onClick={() => setShowProductModal(false)} className="close-btn">✕</button>
            </div>

            <form onSubmit={handleSaveProduct} className="game-form">
              <div className="form-group">
                <label htmlFor="shopName">Name *</label>
                <input
                  type="text"
                  id="shopName"
                  name="name"
                  value={productForm.name}
                  onChange={handleFormChange}
                  required
                  placeholder="z.B. Fanclub-Hoodie"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="shopPrice">Preis in Euro *</label>
                  <input
                    type="number"
                    id="shopPrice"
                    name="price"
                    value={productForm.price}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="shopSortOrder">Sortierung</label>
                  <input
                    type="number"
                    id="shopSortOrder"
                    name="sortOrder"
                    value={productForm.sortOrder}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="shopSizes">Größen *</label>
                <input
                  type="text"
                  id="shopSizes"
                  name="sizes"
                  value={productForm.sizes}
                  onChange={handleFormChange}
                  required
                  placeholder="S, M, L, XL"
                />
                <small>Mit Komma trennen. Genau diese Größen kann das Mitglied auswählen.</small>
              </div>

              <div className="form-group">
                <label htmlFor="shopDescription">Beschreibung</label>
                <textarea
                  id="shopDescription"
                  name="description"
                  value={productForm.description}
                  onChange={handleFormChange}
                  rows="3"
                  placeholder="Material, Passform, Hinweise ..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="shopImage">Bild</label>
                <input
                  type="file"
                  id="shopImage"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageSelect}
                  disabled={uploading}
                />
                <small>JPEG, PNG oder WebP, maximal 2 MB.</small>
                {uploading && <p>Bild wird hochgeladen …</p>}
                {productForm.imageUrl && (
                  <div style={{marginTop: '10px'}}>
                    <img
                      src={productForm.imageUrl}
                      alt="Vorschau"
                      style={{width: '120px', height: '120px', objectFit: 'contain', background: '#f4f4f4', borderRadius: '6px'}}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="active"
                    checked={productForm.active}
                    onChange={handleFormChange}
                  />
                  {' '}Im Shop sichtbar
                </label>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowProductModal(false)} className="btn btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || uploading}>
                  {saving ? 'Speichere …' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopTab;
